/**
 * Connection feedback: is the server answering, slowly, or not at all?
 *
 * Every API request reports its start and end here (see `api/client.ts`; the module's entry point is `index.ts`). The status is worked
 * out from those plus `navigator.onLine`, so it reflects mail actually loading, not a guess.
 * It does NOT poll: a server that dies while the tab sits idle shows up on the next request.
 *
 * 🚩 An HTTP error (4xx/5xx) is NOT "No connection" — the server answered. Only a failed fetch
 * (network error), a request that stalls past STALL_MS, or the browser saying it's offline is.
 */

export type ConnectionStatus = 'idle' | 'loading' | 'slow' | 'offline';
/** 'cancelled' (the user stopped an upload) says nothing about the connection either way. */
export type RequestOutcome = 'ok' | 'http-error' | 'network-error' | 'cancelled';

/** Below this, a request finishes before the bee appears — no flash on every click. */
export const SHOW_DELAY_MS = 400;
/** Still waiting after this long: "Slow connection". */
export const SLOW_MS = 4_000;
/** Still waiting after this long: no data is flowing — "No connection". */
export const STALL_MS = 20_000;

/** 502/503/504 come from the proxy when the mail server can't be reached: no connection. */
export function outcomeForStatus(status: number): RequestOutcome {
  if (status >= 200 && status < 300) return 'ok';
  return status >= 502 && status <= 504 ? 'network-error' : 'http-error';
}

export function createConnectionTracker() {
  // id → start time, for requests whose duration counts (uploads don't: 24 MB is meant to be slow).
  const timed = new Map<number, number>();
  const untimed = new Set<number>();
  let failed = false;
  let online = true;
  // After "Try again", show the bee straight away and keep it until the next request settles.
  let retrying = false;

  return {
    start(id: number, now: number, opts: { timed?: boolean } = {}) {
      if (opts.timed === false) untimed.add(id);
      else timed.set(id, now);
    },
    end(id: number, _now: number, outcome: RequestOutcome) {
      const known = timed.delete(id) || untimed.delete(id);
      if (!known || outcome === 'cancelled') return; // abandoned by retry(), never started, or cancelled
      failed = outcome === 'network-error';
      retrying = false;
    },
    setOnline(value: boolean) { online = value; },
    /** Something could still change with time alone — the store's clock only runs while this is true. */
    busy() { return timed.size > 0 || untimed.size > 0 || retrying; },
    /** "Try again": forget the failure and any stalled requests; the refetch starts fresh. */
    retry(_now: number) {
      failed = false;
      timed.clear();
      untimed.clear();
      retrying = true;
    },
    status(now: number): ConnectionStatus {
      if (!online || failed) return 'offline';
      let oldest = Infinity;
      for (const t of timed.values()) oldest = Math.min(oldest, t);
      const age = oldest === Infinity ? 0 : now - oldest;
      if (age >= STALL_MS) return 'offline';
      if (age >= SLOW_MS) return 'slow';
      if (age >= SHOW_DELAY_MS || retrying) return 'loading';
      return 'idle';
    },
  };
}

// ---- The app's one live tracker ---------------------------------------------
// `api/client.ts` reports requests with trackRequest(); `banner.tsx` reads connectionStore through
// useSyncExternalStore. The snapshot is the status string, so React only re-renders on a change.

const live = createConnectionTracker();
let nextId = 1;
let current: ConnectionStatus = 'idle';
const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

function recompute() {
  // Ages only grow with time, so loading → slow → offline needs a clock — but only while something
  // is in flight. Idle, it stops: no 4 Hz wake-ups on a phone sitting on the inbox.
  if (listeners.size > 0 && live.busy() && !ticker) ticker = setInterval(recompute, 250);
  else if ((!live.busy() || listeners.size === 0) && ticker) { clearInterval(ticker); ticker = null; }
  const next = live.status(Date.now());
  if (next === current) return;
  current = next;
  listeners.forEach((l) => l());
}

/** Call when a request starts; call the returned function exactly once when it settles. */
export function trackRequest(opts: { timed?: boolean } = {}): (outcome: RequestOutcome) => void {
  const id = nextId++;
  live.start(id, Date.now(), opts);
  recompute();
  let done = false;
  return (outcome) => {
    if (done) return;
    done = true;
    live.end(id, Date.now(), outcome);
    recompute();
  };
}

export const connectionStore = {
  subscribe(cb: () => void) {
    listeners.add(cb);
    if (listeners.size === 1) {
      live.setOnline(navigator.onLine);
      window.addEventListener('online', onBrowserOnline);
      window.addEventListener('offline', onBrowserOnline);
      recompute();
    }
    return () => {
      listeners.delete(cb);
      if (listeners.size === 0) {
        window.removeEventListener('online', onBrowserOnline);
        window.removeEventListener('offline', onBrowserOnline);
        recompute();
      }
    };
  },
  getSnapshot: (): ConnectionStatus => current,
  retry() {
    live.retry(Date.now());
    recompute();
  },
};

function onBrowserOnline() {
  live.setOnline(navigator.onLine);
  recompute();
}
